import { z } from 'zod';
import { USER_ROLES } from '../context';
import { socialProviderSchema } from './auth';
import { normalizedUuidSchema, paginationSchema } from './common';

export const userSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  role: z.enum(USER_ROLES),
  emailVerifiedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type User = z.infer<typeof userSchema>;

/**
 * users.me · 관리자 목록이 반환하는 계정 전체 표현.
 *
 * 날짜는 ISO 문자열이다 — tRPC 는 transformer 없이 리졸버 반환 타입을 그대로
 * 클라이언트 타입으로 추론하므로, 서버가 Date 를 반환하면 타입은 Date 인데
 * 와이어는 문자열이 되는 거짓말이 생긴다. 서버는 이 스키마 형태로 직렬화해
 * 반환해야 한다. (apps/api users.router 의 toUserAccount)
 */
export const userAccountSchema = userSchema.extend({
  identityVerifiedAt: z.iso.datetime().nullable(),
  deletedAt: z.iso.datetime().nullable(),
  blockedAt: z.iso.datetime().nullable(),
});

export type UserAccount = z.infer<typeof userAccountSchema>;

/**
 * users.me 출력 — 계정 표현에 서버의 본인인증 사용 여부를 더한다.
 *
 * identityRequired=false(IDENTITY_PROVIDER=disabled)면 화면이 본인인증 단계·안내를 숨기고
 * 클라이언트 상태 파생도 PENDING_IDENTITY 를 건너뛴다. 관리자 목록은 UserAccount 그대로.
 */
export const meAccountSchema = userAccountSchema.extend({
  identityRequired: z.boolean(),
  /** 비밀번호 계정 여부 — 소셜 전용 계정이면 false (프로필 화면이 비밀번호 입력란을 숨길지 정한다). */
  hasPassword: z.boolean(),
});

export type MeAccount = z.infer<typeof meAccountSchema>;

export const updateProfileInputSchema = z.object({
  name: z.string().trim().min(1, '이름을 입력해 주세요.').max(50),
});

export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

export const listUsersInputSchema = paginationSchema.extend({
  role: z.enum(USER_ROLES).optional(),
  search: z.string().trim().max(100).optional(),
});

export type ListUsersInput = z.infer<typeof listUsersInputSchema>;

export const adminGetUserInputSchema = z.object({
  userId: normalizedUuidSchema,
});

export type AdminGetUserInput = z.infer<typeof adminGetUserInputSchema>;

/** 회원 상세의 소셜 연결 하나 — 제공자와 연결 시각. 제공자 계정 id 는 내려주지 않는다. */
export const adminUserSocialAccountSchema = z.object({
  provider: socialProviderSchema,
  linkedAt: z.iso.datetime(),
});

export type AdminUserSocialAccount = z.infer<typeof adminUserSocialAccountSchema>;

/** 회원 상세의 활동 수 — 그 회원이 만든 디자인·견적 요청·주문의 전체 건수(상태 무관). */
export const adminUserActivitySchema = z.object({
  designCount: z.number().int().nonnegative(),
  quoteRequestCount: z.number().int().nonnegative(),
  orderCount: z.number().int().nonnegative(),
});

export type AdminUserActivity = z.infer<typeof adminUserActivitySchema>;

/**
 * users.adminGet 출력 — 계정 표현에 차단 사유·로그인 수단·활동 수를 더한다.
 *
 * 관리자 회원 상세(/users/:userId)가 쓴다. 날짜는 UserAccount 와 같이 ISO 문자열이다.
 */
export const adminUserDetailSchema = userAccountSchema.extend({
  /** 차단 사유 — 차단되지 않았으면 null. */
  blockedReason: z.string().nullable(),
  /** 비밀번호 계정 여부 — 소셜 전용 계정이면 false. */
  hasPassword: z.boolean(),
  /** 연결된 소셜 계정 — 연결 시각 오름차순. */
  socialAccounts: z.array(adminUserSocialAccountSchema),
  activity: adminUserActivitySchema,
});

export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

// 운영진 차단 — 사유는 blocked_reason 컬럼과 감사 로그 detail 에 함께 남는다.
export const blockUserInputSchema = z.object({
  reason: z.string().trim().min(1, '차단 사유를 입력해 주세요.').max(200),
});

export type BlockUserInput = z.infer<typeof blockUserInputSchema>;
